import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import Badge from "../components/Badge";
import IconButton from "../components/IconButton";
import ProgressBar from "../components/ProgressBar";
import { PlayIcon, CheckIcon, AudioIcon } from "../components/icons";
import { getSeries, getCatalogCounts } from "../api/redesignApi";
import { LikeButton, BookmarkButton } from "../components/EngagementButtons";
import { fetchSeriesProgress, isSignedIn } from "../api/progressSync";
import { getPositionSeconds } from "../api/positionSeconds";
import { paths } from "../routing/paths";
import { trackEvent } from "../../analytics";

/*
 * Redesigned series page (Series Page reference) — two coexisting views of
 * the same content with a persistent toggle (brief §7.1):
 *   Learning view — navy hero (lockup, description, counts, progress bar,
 *   Resume / Start from the beginning), course outline in three states
 *   (completed ✓ / current UP NEXT with blue left border / future numbered).
 *   Archive view — the honest tree: expandable sub-series nodes with counts,
 *   sort chips, dense leaf rows.
 *
 * Progress states derive from device-local resume positions until the
 * Phase 6 progress service lands (per-class media URLs aren't in the series
 * payload, so "started" is inferred from the class pages already visited —
 * documented in BACKEND-TODO.md). Row play buttons navigate to the class
 * page, which resumes from the saved position.
 */

const VIEW_KEY = "ic-series-view";

function stripSeriesPrefix(title, seriesTitle) {
  return title;
}

function useSeriesData(slug) {
  const [state, setState] = useState({ status: "loading", series: null });
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", series: null });
    getSeries(slug)
      .then((series) => !cancelled && setState({ status: "done", series }))
      .catch(() => !cancelled && setState({ status: "done", series: null }));
    return () => {
      cancelled = true;
    };
  }, [slug]);
  return state;
}

function ViewToggle({ view, setView }) {
  const btn = (key, label, icon, active) => (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => setView(key)}
      className={
        active
          ? "h-10 px-5 font-sans text-[14px] font-bold leading-5 text-ic-offwhite bg-ic-navy border-0 rounded-3xl cursor-pointer shadow-ic-raised inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:shadow-ic-focus"
          : "h-10 px-5 font-sans text-[14px] font-semibold leading-5 text-ic-body bg-transparent border-0 rounded-3xl cursor-pointer inline-flex items-center gap-1.5 transition-colors duration-150 ease-out hover:text-ic-blue focus-visible:outline-none focus-visible:shadow-ic-focus"
      }
    >
      {icon}
      {label}
    </button>
  );
  return (
    <div role="tablist" aria-label="Discovery mode or classic view" className="inline-flex bg-ic-lightGray rounded-3xl p-1 gap-1 flex-shrink-0">
      {btn("learning", "Discovery mode", <PlayIcon size={14} />, view === "learning")}
      {btn(
        "archive",
        "Classic view",
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M4 6h16" />
          <path d="M4 12h16" />
          <path d="M4 18h16" />
        </svg>,
        view === "archive"
      )}
    </div>
  );
}

/* ---------------- Learning view ---------------- */

function LearningView({ series, progress }) {
  const navigate = useNavigate();
  const classes = series.children.filter((c) => c.type === "class");
  const subSeries = series.children.filter((c) => c.type === "series");
  const { startedIds, completedIds, currentId } = progress;

  const currentIdx = classes.findIndex((c) => String(c.id) === currentId);
  // Real completed states when the progress service supplied them; otherwise
  // the device heuristic (every started class except the current one).
  const completedCount =
    completedIds.size > 0 ? completedIds.size : startedIds.size > 0 ? Math.max(0, startedIds.size - 1) : 0;
  const pct = classes.length > 0 ? Math.round((completedCount / classes.length) * 100) : 0;

  const resumeTarget = currentIdx >= 0 ? classes[currentIdx] : null;

  const rowState = (c) => {
    if (String(c.id) === currentId) return "current";
    if (completedIds.has(String(c.id)) || startedIds.has(String(c.id))) return "done";
    return "future";
  };

  const [showAll, setShowAll] = useState(classes.length <= 12);
  const visible = showAll ? classes : classes.slice(0, 10);

  return (
    <div className="flex flex-col gap-6">
      {/* Hero */}
      <section className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex flex-col gap-4" style={{ padding: "clamp(20px, 5vw, 32px)" }}>
        <div className="flex gap-5 items-start flex-wrap">
          <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-ic-navyCard hidden xs:flex flex-col items-center justify-center gap-0.5">
            <span dir="rtl" className="text-[30px] font-extrabold text-ic-sky leading-9">סדרה</span>
            <span className="text-[9px] font-bold tracking-[0.5px] text-ic-lightGray uppercase">Series</span>
          </div>
          <div className="flex-1 min-w-[240px] flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Badge variant="primary">SERIES</Badge>
              {series.id && (
                <span className="inline-flex items-center gap-2">
                  {/* Like + bookmark on the series itself (v2.2 §12, navy) */}
                  <LikeButton target="series" id={series.id} surface="navy" />
                  <BookmarkButton target="series" id={series.id} surface="navy" />
                </span>
              )}
            </div>
            <h1 className="m-0 font-bold text-white tracking-[-0.4px] ic-wrap-anywhere" style={{ fontSize: "clamp(24px, 5.5vw, 36px)", lineHeight: 1.25 }}>
              {series.title}
            </h1>
            {series.description && (
              <p className="m-0 text-[18px] font-normal leading-7 text-ic-offwhite max-w-[620px]">{series.description}</p>
            )}
            <span className="text-[14px] leading-5 text-ic-lightGray">
              {[
                classes.length > 0 ? `${classes.length} classes` : null,
                subSeries.length > 0 ? `${subSeries.length} sub-series` : null,
                "Rabbi Yossi Paltiel",
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        </div>

        {classes.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-white/10 pt-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <ProgressBar value={pct} theme="dark" height={6} />
              </div>
              <span className="text-[14px] font-semibold leading-5 text-ic-offwhite flex-shrink-0">
                {completedCount} of {classes.length} completed
              </span>
            </div>
            <div className="flex gap-3 flex-wrap items-center">
              {resumeTarget ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate(paths.permalink(resumeTarget.slug))}
                    className="bg-ic-blue text-ic-offwhite font-sans text-[18px] font-bold leading-7 px-8 h-12 border-0 rounded-3xl cursor-pointer inline-flex items-center gap-2 transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive active:shadow-ic-inset focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                  >
                    <PlayIcon size={18} /> Resume — Class {currentIdx + 1} of {classes.length}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(paths.permalink(classes[0].slug))}
                    className="bg-transparent text-ic-offwhite font-sans text-[16px] font-semibold leading-6 px-6 h-12 border border-ic-lightGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                  >
                    Start from the beginning
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => classes[0] && navigate(paths.permalink(classes[0].slug))}
                  className="bg-ic-blue text-ic-offwhite font-sans text-[18px] font-bold leading-7 px-8 h-12 border-0 rounded-3xl cursor-pointer inline-flex items-center gap-2 transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive active:shadow-ic-inset focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                >
                  <PlayIcon size={18} /> Start from the beginning
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Sub-series (when this series is a container) */}
      {subSeries.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
            In this series
          </h2>
          <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
            {subSeries.map((s, i) => (
              <Link
                key={s.id}
                to={paths.series(s.slug)}
                className={`flex items-center gap-3 px-4 py-3.5 ${i === subSeries.length - 1 ? "" : "border-b border-ic-lightGray"} no-underline hover:no-underline transition-colors duration-150 ease-out hover:bg-ic-offwhite`}
              >
                <span className="flex-shrink-0"><Badge variant="primary">SERIES</Badge></span>
                <span className="flex-1 min-w-0 text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{s.title}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#186CCE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Course outline */}
      {classes.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
            Series outline
          </h2>
          <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
            {visible.map((c, idx) => {
              const state = rowState(c, idx);
              const last = idx === visible.length - 1 && showAll;
              if (state === "current") {
                return (
                  <div key={c.id} className={`flex items-center gap-3 p-4 ${last ? "" : "border-b border-ic-lightGray"} bg-[rgba(24,108,206,0.05)] border-l-4 border-l-ic-blue`}>
                    <span aria-label="Up next" className="flex-shrink-0 w-11 h-11 rounded-full bg-ic-blue inline-flex items-center justify-center text-ic-offwhite text-[14px] font-bold">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={paths.permalink(c.slug)} className="text-[18px] font-semibold leading-7 text-black no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                          {stripSeriesPrefix(c.title, series.title)}
                        </Link>
                        <span className="bg-ic-blue text-white text-[11px] font-bold tracking-[0.5px] px-2.5 py-0.5 rounded-xl flex-shrink-0">UP NEXT</span>
                      </div>
                      {c.dateLabel && <span className="text-[14px] leading-5 text-ic-slate">{c.dateLabel}</span>}
                    </div>
                    <IconButton variant="solid" size={48} label="Resume this class" onClick={() => window.location.assign(paths.permalink(c.slug))}>
                      <PlayIcon size={20} />
                    </IconButton>
                  </div>
                );
              }
              if (state === "done") {
                return (
                  <div key={c.id} className={`flex items-center gap-3 px-4 py-3 ${last ? "" : "border-b border-ic-lightGray"} transition-colors duration-150 ease-out hover:bg-ic-offwhite`}>
                    <span aria-label="Completed" className="flex-shrink-0 w-11 h-11 rounded-full bg-[rgba(36,154,26,0.1)] inline-flex items-center justify-center text-ic-success">
                      <CheckIcon size={18} strokeWidth={2} />
                    </span>
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                      <Link to={paths.permalink(c.slug)} className="text-[18px] font-semibold leading-7 text-ic-slate no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                        {idx + 1}. {stripSeriesPrefix(c.title, series.title)}
                      </Link>
                      <span className="text-[14px] leading-5 text-ic-slate">{[c.dateLabel, "Started"].filter(Boolean).join(" · ")}</span>
                    </div>
                    <IconButton variant="circleOutline" size={44} label="Play again" onClick={() => window.location.assign(paths.permalink(c.slug))}>
                      <PlayIcon size={16} />
                    </IconButton>
                  </div>
                );
              }
              return (
                <div key={c.id} className={`flex items-center gap-3 px-4 py-3 ${last ? "" : "border-b border-ic-lightGray"} transition-colors duration-150 ease-out hover:bg-ic-offwhite`}>
                  <span className="flex-shrink-0 w-11 h-11 rounded-full bg-ic-offwhite border border-ic-lightGray inline-flex items-center justify-center text-ic-slate text-[14px] font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <Link to={paths.permalink(c.slug)} className="text-[18px] font-semibold leading-7 text-black no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                      {stripSeriesPrefix(c.title, series.title)}
                    </Link>
                    {c.dateLabel && <span className="text-[14px] leading-5 text-ic-slate">{c.dateLabel}</span>}
                  </div>
                  <IconButton variant="circleOutline" size={44} label="Play this class" onClick={() => window.location.assign(paths.permalink(c.slug))}>
                    <PlayIcon size={16} />
                  </IconButton>
                </div>
              );
            })}
            {!showAll && (
              <div className="flex justify-center p-4">
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="bg-transparent text-ic-body font-sans text-[16px] font-semibold leading-6 px-6 py-2.5 min-h-[48px] border border-ic-borderGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue active:bg-black/5 focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                >
                  Show all {classes.length} classes
                </button>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------------- Archive view ---------------- */

const SORTS = [
  { key: "order", label: "Series order" },
  { key: "date", label: "Date" },
  { key: "az", label: "A–Z" },
];

function sortChildren(children, sort) {
  const copy = [...children];
  if (sort === "date") copy.sort((a, b) => String(a.dateLabel).localeCompare(String(b.dateLabel)) || a.order - b.order);
  else if (sort === "az") copy.sort((a, b) => String(a.title).localeCompare(String(b.title)));
  else copy.sort((a, b) => a.order - b.order);
  return copy;
}

function ArchiveNode({ node, sort, depth = 0 }) {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState(null);

  useEffect(() => {
    if (open && children === null) {
      getSeries(node.slug).then((s) => setChildren(s ? s.children : []));
    }
  }, [open, children, node.slug]);

  const count = children === null ? null : children.length;

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2.5 px-4 py-3 ${open ? "bg-ic-offwhite" : "bg-white"} border-0 border-b border-ic-lightGray font-sans cursor-pointer text-left min-h-[48px] box-border transition-colors duration-150 ease-out hover:bg-ic-lightGray/60 focus-visible:outline-none focus-visible:shadow-[inset_0px_0px_0px_3px_rgba(24,108,206,0.1)]`}
        style={{ paddingLeft: 16 + depth * 20 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={open ? "#186CCE" : "#515F7B"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 150ms ease-out" }}>
          <path d="M9 6l6 6-6 6" />
        </svg>
        <span className={`text-[16px] leading-6 ${open ? "font-bold text-black" : "font-semibold text-ic-navy"} ic-wrap-anywhere`}>{node.title}</span>
        <span className="bg-ic-lightGray text-ic-body text-[12px] font-semibold px-2.5 py-0.5 rounded-xl flex-shrink-0">
          {count === null ? "…" : `${count} items`}
        </span>
      </button>
      {open && children && (
        <div className="border-b border-ic-lightGray">
          {sortChildren(children, sort).map((leaf, i) =>
            leaf.type === "series" ? (
              <ArchiveNode key={leaf.id} node={leaf} sort={sort} depth={depth + 1} />
            ) : (
              <LeafRow key={leaf.id} leaf={leaf} idx={i + 1} depth={depth + 1} />
            )
          )}
          {children.length === 0 && (
            <div className="px-10 py-3 text-[14px] text-ic-slate">Nothing filed under this node yet.</div>
          )}
        </div>
      )}
    </>
  );
}

function LeafRow({ leaf, idx, depth = 0 }) {
  return (
    <div
      className="flex items-center gap-2.5 pr-4 py-2 border-b border-[#F3F4F6] min-h-[44px] box-border transition-colors duration-150 ease-out hover:bg-ic-offwhite"
      style={{ paddingLeft: 42 + depth * 20 }}
    >
      <span className="flex-shrink-0 w-7 text-[13px] tabular-nums text-ic-slate" style={{ fontFamily: "'Courier New',monospace" }}>
        {String(idx).padStart(2, "0")}
      </span>
      <Link
        to={paths.permalink(leaf.slug)}
        className="flex-1 min-w-0 text-[15px] font-semibold leading-[22px] text-ic-navy no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere py-2 -my-2 inline-flex items-center min-h-[44px]"
      >
        {leaf.title}
      </Link>
      {leaf.dateLabel && <span className="flex-shrink-0 text-[13px] text-ic-slate w-16 text-right">{leaf.dateLabel}</span>}
      <span aria-label="Audio" className="flex-shrink-0 text-ic-slate inline-flex">
        <AudioIcon size={16} />
      </span>
    </div>
  );
}

function ArchiveView({ series }) {
  const [sort, setSort] = useState("order");
  const [deepCounts, setDeepCounts] = useState(null);
  const classes = series.children.filter((c) => c.type === "class");
  const subSeries = series.children.filter((c) => c.type === "series");

  useEffect(() => {
    getCatalogCounts(series.slug).then(setDeepCounts);
  }, [series.slug]);

  const summary = deepCounts
    ? `${subSeries.length} sub-series · ${deepCounts.classCount.toLocaleString()} classes · every recording, in order`
    : [
        subSeries.length > 0 ? `${subSeries.length} sub-series` : null,
        classes.length > 0 ? `${classes.length} classes` : null,
        "every recording, in order",
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-0.5">
          <h1 className="m-0 font-bold text-black tracking-[-0.3px] ic-wrap-anywhere" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
            {series.title} — the complete archive
          </h1>
          <span className="text-[14px] leading-5 text-ic-slate">{summary}</span>
        </div>
        <div className="ic-rail flex gap-2 overflow-x-auto items-center">
          <span className="flex-shrink-0 text-[12px] font-bold tracking-[0.5px] uppercase text-ic-slate">Sort</span>
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-pressed={sort === s.key}
              onClick={() => setSort(s.key)}
              className={
                sort === s.key
                  ? "flex-shrink-0 font-sans text-[14px] font-bold leading-5 text-white bg-ic-blue border border-ic-blue px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] focus-visible:outline-none focus-visible:shadow-ic-focus"
                  : "flex-shrink-0 font-sans text-[14px] font-semibold leading-5 text-ic-body bg-white border border-ic-borderGray px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
              }
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
        {sortChildren(subSeries, sort).map((node) => (
          <ArchiveNode key={node.id} node={node} sort={sort} />
        ))}
        {sortChildren(classes, sort).map((leaf, i) => (
          <LeafRow key={leaf.id} leaf={leaf} idx={i + 1} />
        ))}
        {series.children.length === 0 && (
          <div className="px-6 py-8 text-center text-[14px] text-ic-slate">Nothing filed under this series yet.</div>
        )}
      </div>
      <p className="m-0 text-[14px] leading-5 text-ic-slate">
        Counts shown at every node. Keyboard: <span style={{ fontFamily: "'Courier New',monospace" }}>Tab</span> moves
        between sections, <span style={{ fontFamily: "'Courier New',monospace" }}>Enter</span> expands.
      </p>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function SeriesPage() {
  const { uniqueName } = useParams();
  const { status, series } = useSeriesData(uniqueName);
  const [view, setViewState] = useState(() => localStorage.getItem(VIEW_KEY) || "learning");
  const setView = (v) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
      trackEvent({ category: "series", action: "view_toggle", label: v });
    } catch (_) {}
  };

  // Progress states. Device-local inference renders immediately (classes
  // whose pages recorded a resume position); when signed in, the Phase 6
  // series-progress rollup replaces it with real completed/current states —
  // falling back silently until that endpoint deploys.
  const deviceProgress = useMemo(() => {
    const startedIds = new Set();
    let currentId = null;
    if (!series) return { startedIds, completedIds: new Set(), currentId };
    const classes = series.children.filter((c) => c.type === "class");
    let best = -1;
    for (const c of classes) {
      const marker = localStorage.getItem(`ic-class-media:${c.slug}`);
      const pos = marker ? getPositionSeconds(marker) : 0;
      if (pos > 0) {
        startedIds.add(String(c.id));
        const idx = classes.indexOf(c);
        if (idx > best) {
          best = idx;
          currentId = String(c.id);
        }
      }
    }
    return { startedIds, completedIds: new Set(), currentId };
  }, [series]);

  const [serverProgress, setServerProgress] = useState(null);
  useEffect(() => {
    setServerProgress(null);
    if (series && isSignedIn()) {
      fetchSeriesProgress(uniqueName).then((sp) => {
        if (!sp) return;
        setServerProgress({
          startedIds: new Set(sp.classes.filter((c) => c.position_seconds > 0).map((c) => String(c.class_id))),
          completedIds: new Set(sp.classes.filter((c) => c.completed).map((c) => String(c.class_id))),
          currentId: sp.current_class_id != null ? String(sp.current_class_id) : null,
        });
      });
    }
  }, [series, uniqueName]);

  const progress = serverProgress || deviceProgress;

  if (status === "loading") {
    return (
      <PublicLayout>
        <div className="max-w-[960px] w-full mx-auto px-5 pt-8 flex flex-col gap-4" aria-busy="true">
          <div className="h-10 w-56 rounded-3xl bg-ic-lightGray animate-pulse self-end" />
          <div className="h-56 rounded-xl bg-ic-navy/90 animate-pulse" />
          <div className="h-72 rounded-xl bg-ic-lightGray animate-pulse" />
        </div>
      </PublicLayout>
    );
  }

  if (!series) {
    return (
      <PublicLayout>
        <div className="max-w-[640px] w-full mx-auto px-5 py-16 flex flex-col items-center gap-4 text-center">
          <h1 className="m-0 text-[28px] font-bold leading-9 tracking-[-0.3px] text-black">Series not found</h1>
          <p className="m-0 text-[18px] leading-7 text-ic-body">
            This series may have moved. Search will find it — with 15,000+ classes, it's in there.
          </p>
          <Link to={paths.search()} className="text-ic-blue text-[16px] font-semibold min-h-[44px] inline-flex items-center hover:underline">
            Search the archive
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <main className="max-w-[960px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <nav aria-label="Breadcrumb" className="ic-rail flex items-center gap-1 overflow-x-auto text-[14px] leading-5 whitespace-nowrap">
            <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
              Home
            </Link>
            <span className="text-ic-slate">→</span>
            <span className="text-ic-body font-semibold px-1 ic-wrap-anywhere">{series.title}</span>
          </nav>
          <ViewToggle view={view} setView={setView} />
        </div>
        {view === "learning" ? <LearningView series={series} progress={progress} /> : <ArchiveView series={series} />}
      </main>
    </PublicLayout>
  );
}
